import { Monitor, Moon, Sun } from 'lucide-react'
import type { ComponentProps, ComponentType } from 'react'
import type { MessageKey } from '~/i18n'
import { useT } from '~/lib/i18n'
import { type ThemePreference, useTheme } from '~/lib/theme'
import { cn } from '~/lib/utils'

const THEMES: {
  value: ThemePreference
  label: MessageKey
  icon: ComponentType<{ className?: string }>
}[] = [
  { value: 'light', label: 'theme.light', icon: Sun },
  { value: 'dark', label: 'theme.dark', icon: Moon },
  { value: 'system', label: 'theme.match_device', icon: Monitor },
]

export function ThemeSwitch({ className, ...props }: ComponentProps<'fieldset'>) {
  const t = useT()
  const { preference, setPreference } = useTheme()
  return (
    <fieldset
      className={cn('flex rounded-control border border-border bg-surface-muted p-0.5', className)}
      {...props}
    >
      <legend className="sr-only">{t('common.appearance')}</legend>
      {THEMES.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          onClick={() => setPreference(value)}
          aria-pressed={preference === value}
          title={t(label)}
          className={cn(
            // `min-w` rather than a width: it still stretches where the fieldset has
            // room (Settings), and stays thumb-sized where it is sized to its own
            // content (beside the language switch in the sidebar).
            'flex h-8 min-w-8 flex-1 items-center justify-center rounded-[0.4375rem] transition-colors',
            preference === value
              ? 'bg-surface text-primary shadow-card'
              : 'text-ink-subtle hover:text-ink',
          )}
        >
          <Icon className="size-4" />
          <span className="sr-only">{t(label)}</span>
        </button>
      ))}
    </fieldset>
  )
}
